import { InputHTMLAttributes, ReactNode, useMemo } from "react"

import { Input as InputPrimitive } from "@base-ui/react/input"
import { Slot } from "@radix-ui/react-slot"
import { IMaskInput, IMaskMixin } from "react-imask"

import { cn } from "@/lib/utils"

export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "prefix"
> {
  isError?: boolean
  value?: string
  asChild?: boolean
  prefix?: ReactNode
  suffix?: ReactNode
  rootClassName?: string
  ref?: React.Ref<HTMLInputElement>
}

export function Input({
  isError,
  prefix,
  suffix,
  className,
  rootClassName,
  placeholder,
  asChild,
  id,
  ref,
  ...inputProps
}: InputProps) {
  const Comp = asChild ? Slot : InputPrimitive

  return (
    <div
      className={cn(
        "flex flex-row",
        "w-full rounded border border-solid border-input bg-transparent outline-none",
        "text-foreground hover:border-accent-foreground hover:bg-accent data-[hover=true]:border-accent-foreground data-[hover=true]:bg-accent/20",
        "focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 data-[focus=true]:border-accent-foreground",
        "aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:bg-input/50 aria-disabled:opacity-50",
        "aria-invalid:border-destructive aria-invalid:focus:border-destructive",
        "aria-invalid:ring-3 aria-invalid:ring-destructive/20",
        rootClassName,
      )}
      aria-invalid={isError}
      aria-disabled={inputProps.disabled}
      {...inputProps}
      onBlur={undefined}
      data-testid={undefined}
    >
      {prefix ? (
        <div className="ml-3 flex items-center whitespace-nowrap">{prefix}</div>
      ) : null}

      <Comp
        id={id}
        ref={ref}
        aria-invalid={isError}
        className={cn(
          "w-full bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground/80 disabled:placeholder:text-muted-foreground/60",
          "transition-colors",
          className,
        )}
        placeholder={placeholder}
        {...inputProps}
      />

      {suffix ? (
        <div className="mr-3 flex items-center whitespace-nowrap">{suffix}</div>
      ) : null}
    </div>
  )
}

const BaseMaskedInput = IMaskMixin(({ inputRef, value, ...props }) => (
  <Input value={value?.toString()} {...props} ref={inputRef} />
)) as typeof IMaskInput

export const MaskedInput = (({ value, ...props }) => {
  // ensure value is always a string before it goes into IMaskMixin
  const strValue = useMemo(() => {
    return value?.toString() ?? ""
  }, [value])

  // set default radix and thousandsSeparator for every block
  const blocks = Object.fromEntries(
    //@ts-expect-error messy typing, blocks exists
    Object.entries(props?.blocks ?? {}).map(([key, config]) => [
      key,
      { radix: ".", thousandsSeparator: ",", ...(config as object) },
    ]),
  )

  return (
    <BaseMaskedInput
      value={strValue}
      thousandsSeparator=","
      radix="."
      {...props}
      blocks={blocks}
    />
  )
}) as typeof IMaskInput
